/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { FlowIfDisabled, FlowIfEnabled } from '@/components/blocks/FlexFlow';
import { OneClick } from '@/components/blocks/OneClick';
import { productDetailsAddToCartErrorSX } from '@/components/blocks/ProductDetails/styles/addToCartError';
import { productDetailsAddToCartStack } from '@/components/blocks/ProductDetails/styles/addToCartStack';
import { EMS_STORE_FEATURE } from '@/data/constants/flexFlowStoreFeature';
import { useBundleDetailsTable } from '@/data/Content/BundleDetailsTable';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useSkuListTable } from '@/data/Content/SkuListTable';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { getProductDisplayInfo } from '@/utils/getProductDisplayInfo';
import { isKitOrBundleType } from '@/utils/productIsA';
import { Button, Stack, Typography } from '@mui/material';
import { FC, useContext, useMemo } from 'react';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import { useStoreLocatorState } from '@/data/state/useStoreLocatorState';
import { basketIconPDPSX, addtocartbuttonSX } from '@/components/blocks/ProductDetails/styles/addToCartStack';

type Props = {
	standalone?: boolean;
};
export const ProductDetailsAddToCart: FC<Props> = ({ standalone = false }) => {
	const { AddToCart, SignIn } = useLocalization('productDetail');
	const {
		selection,
		hasInventory,
		addToCart,
		product,
		loginStatus,
		error = {},
		isSkuListTableDisplayed,
		storeLocator,
		isPickupOnlyItem
	} = useContext(ContentContext) as ReturnType<typeof useProductDetails> &
		ReturnType<typeof useBundleDetailsTable> &
		ReturnType<typeof useSkuListTable> &
		ReturnType<typeof useStoreLocatorState>;

	const disabledButton = useMemo(() => {
		const isKitOrBundle = isKitOrBundleType[product?.type as string];

		const sku = !isKitOrBundle ? selection?.sku : product;
		const { prices } = getProductDisplayInfo(sku, product);

		const buyable = !isKitOrBundle ? selection?.buyable : true;

		const otherDisabled = !buyable || !hasInventory || !prices?.offer;

		const skuListEmpty = product?.items?.length === 0;
		const isDisableHD  = isSkuListTableDisplayed ? skuListEmpty : otherDisabled;

		const isCCStoreSelected = Boolean(storeLocator?.selectedStore?.id);

		const isDisableForStore  = buyable && hasInventory ? !isCCStoreSelected : true;

		if (isPickupOnlyItem) {
			return !isCCStoreSelected;
		}

		return isDisableHD && isDisableForStore;
	}, [
		product,
		storeLocator?.selectedStore?.id,
		selection,
		hasInventory,
		isSkuListTableDisplayed,
		isKitOrBundleType,
		isPickupOnlyItem
	]);

	const dtId = useMemo(() => `product-add-to-cart${standalone ? '-standalone' : ''}`, [standalone]);

	return (
		<Stack spacing={2} width="100%">
			<Stack {...productDetailsAddToCartStack(standalone)}>
				{/* <ProductDetailsAddToWishList standalone={standalone} />
				<ProductDetailsAddToRequisitionList standalone={standalone} /> */}
				<OneClick
					data-testid={dtId}
					id={dtId}
					onClick={addToCart}
					variant="primaryConversion"
					disabled={disabledButton}
					sx={addtocartbuttonSX}
				>
					<ShoppingBasketIcon sx={basketIconPDPSX}/>					
					<FlowIfEnabled feature={EMS_STORE_FEATURE.GUEST_SHOPPING}><Typography component="span">{AddToCart.t()}</Typography></FlowIfEnabled>
					<FlowIfDisabled feature={EMS_STORE_FEATURE.GUEST_SHOPPING}>
						<Typography component="span">{loginStatus ? AddToCart.t() : SignIn.t()}</Typography>
					</FlowIfDisabled>
				</OneClick>
			</Stack>
			{error.message ? (
				<Typography variant="body2" sx={productDetailsAddToCartErrorSX}>
					{error.message}
				</Typography>
			) : null}
		</Stack>
	);
};
